
































































































































import {
	computed,
	defineComponent,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { VueConstructor } from 'vue';
import { getLearningProgramsStore } from '@front-app/modules/learningPrograms/store/LearningProgramsStore';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import { useIsError, useIsLoading } from '@front-app/hooks/useAsyncStatus';
import LoaderState from '@front-app/components/LoaderState.vue';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import AsyncSpeakerList from '@front-app/modules/learningPrograms/components/AsyncSpeakerList.vue';
import LearningMeta from '@front-app/modules/learningPrograms/components/LearningMeta.vue';
import { useAsyncModal } from '@front-app/hooks/useAsyncModal';
import { LearningStep } from '@front-app/modules/learningPrograms/Model';
import BackButton from '@front-app/components/buttons/BackButton.vue';
import { Routes } from '@front-app/constants/routes';
import { CompletedStepEvent } from '@front-app/modules/learningPrograms/stepModal/stepEvents';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { getIsCompleted } from '@front-app/modules/learningPrograms/util/util';
import SuccessCheckmark from '@front-app/components/icons/SuccessCheckmark.vue';
import CompletedBadge from '@front-app/modules/learningPrograms/components/CompletedBadge.vue';

const getStepModal = () => import('@front-app/modules/learningPrograms/stepModal/StepModal.vue')
	.then((_) => _.default as VueConstructor);

export default defineComponent({
	components: {
		PaddedContent,
		ErrorCard,
		LoaderState,
		SimpleCard,
		CtaButton,
		AsyncSpeakerList,
		LearningMeta,
		BackButton,
		SuccessCheckmark,
		CompletedBadge,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
		programId: {
			type: String,
			required: true,
		},
		moduleId: {
			type: String,
			required: true,
		},
	},
	setup(props) {
		// Init and fetches
		const programsStore = getLearningProgramsStore();
		const isCompleted = getIsCompleted(programsStore);
		watch(() => props.groupId, () => {
			programsStore.fetchProgram(props.programId, false, CurrentUserStore.isCsr.value);
		}, { immediate: true });
		const isLoadingModule = computed(() => useIsLoading(programsStore.getProgramStatus(props.programId)).value);
		const isErrorModule = computed(() => useIsError(programsStore.getProgramStatus(props.programId)).value);

		// Basic display
		const learningModule = computed(() => programsStore.getModule(props.moduleId));
		const parts = computed(() => learningModule.value.parts.map(programsStore.getPart));
		const getSteps = (partId: string) => programsStore.getPart(partId).steps.map(programsStore.getStep);

		// Step modal viewer
		const selectedStepId: Ref<null | string> = ref(null);
		const stepModal = useAsyncModal('', getStepModal);
		const openStep = (e: MouseEvent, step: LearningStep) => {
			selectedStepId.value = step.id;
			stepModal.showModal(e.target as HTMLButtonElement);
		};
		const closeStep = () => {
			stepModal.closeModal();
			selectedStepId.value = null;
		};
		const completeStep = (e: CompletedStepEvent) => {
			programsStore.completeStep(e.stepId, props.groupId);
		};

		return {
			learningModule,
			parts,
			getSteps,
			isLoadingModule,
			isErrorModule,
			toProgram: { name: Routes.GROUP_LEARNING_PROGRAM },
			// Step buttons
			unstartedStepButtonPalette: ButtonPalettes.PRIMARY,
			completedStepButtonPalette: ButtonPalettes.SUBDUED,
			stepButtonSize: Size.LARGE,
			// Step Modal
			selectedStepId,
			openStep,
			stepModalComponent: stepModal.modalView,
			closeStep,
			completeStep,
			isStepModalVisible: stepModal.isModalVisible,
			// Util
			isCompleted,
		};
	},
});
